import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { biz, type AgingReport, type Dashboard, type GstReport } from '../../lib/business'
import { money } from '../../lib/format'

export default function HomePage() {
  const [d, setD] = useState<Dashboard | null>(null)
  const [ar, setAr] = useState<AgingReport | null>(null)
  const [ap, setAp] = useState<AgingReport | null>(null)
  const [gst, setGst] = useState<GstReport | null>(null)
  const [gstConfigured, setGstConfigured] = useState(true)

  useEffect(() => {
    biz.dashboard().then(setD).catch(() => {})
    biz.receivables().then(setAr).catch(() => {})
    biz.payables().then(setAp).catch(() => {})
    biz.gst().then(setGst).catch(() => {})
    biz.company().then((c) => setGstConfigured(c.gst_configured)).catch(() => {})
  }, [])

  if (!d) return <div className="skeleton" style={{ height: 140 }} />

  const stats = [
    { label: 'Revenue this month', value: money(d.revenue_mtd), note: 'invoiced' },
    {
      label: 'Receivable', value: money(d.receivables_outstanding),
      note: 'customers owe us', tone: d.receivables_outstanding > 0 ? 'warn' : undefined,
    },
    {
      label: 'Payable', value: money(d.payables_outstanding),
      note: 'we owe vendors', tone: d.payables_outstanding > 0 ? 'warn' : undefined,
    },
    {
      label: 'Open quotations',
      value: String(d.open_quotations.count),
      note: money(d.open_quotations.value),
    },
    {
      label: 'Open leads', value: String(d.open_leads.count),
      note: money(d.open_leads.value),
    },
    {
      label: 'Awaiting review',
      value: String(d.documents_needing_review),
      note: 'uploaded invoices',
      tone: d.documents_needing_review > 0 ? 'warn' : undefined,
    },
  ]

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {!gstConfigured && (
        <div className="card" style={{ borderLeft: '3px solid var(--warn)' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                Add your company GSTIN
              </div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                Without it we cannot tell an inter-state sale (IGST) from an
                intra-state one (CGST + SGST), so raising documents is blocked.
              </div>
            </div>
            <Link className="btn btn-primary" to="/settings">Open settings</Link>
          </div>
        </div>
      )}

      <div className="stat-row">
        {stats.map((s) => (
          <div className="stat" key={s.label}>
            <div className="stat-label">{s.label}</div>
            <div
              className="stat-value"
              style={s.tone === 'warn' ? { color: 'var(--warn)' } : undefined}
            >
              {s.value}
            </div>
            <div className="stat-note">{s.note}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
           className="two-col">
        <AgingCard title="Receivables ageing" report={ar} nameKey="by_contact" />
        <AgingCard title="Payables ageing" report={ap} nameKey="by_vendor" />
      </div>

      {gst && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">GST summary — {gst.period.start} to {gst.period.end}</span>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Rate</th><th className="num">Taxable</th>
                  <th className="num">CGST</th><th className="num">SGST</th>
                  <th className="num">IGST</th><th className="num">Total tax</th>
                </tr>
              </thead>
              <tbody>
                {gst.output_tax_by_rate.length === 0 ? (
                  <tr><td colSpan={6} className="muted">No invoices in this period.</td></tr>
                ) : gst.output_tax_by_rate.map((b) => (
                  <tr key={b.gst_rate}>
                    <td>{b.gst_rate}%</td>
                    <td className="num">{money(b.taxable_value)}</td>
                    <td className="num">{money(b.cgst)}</td>
                    <td className="num">{money(b.sgst)}</td>
                    <td className="num">{money(b.igst)}</td>
                    <td className="num">{money(b.total_tax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-body" style={{ borderTop: '1px solid var(--line)' }}>
            <div className="wrap-gap" style={{ fontSize: 13 }}>
              <span>Output tax <strong>{money(gst.output_tax_total)}</strong></span>
              <span>Input credit <strong>{money(gst.input_tax_total)}</strong>
                <span className="muted"> ({gst.input_bill_count} bills)</span></span>
              <span>Net {gst.net_payable >= 0 ? 'payable' : 'credit'}{' '}
                <strong style={{ color: gst.net_payable >= 0 ? 'var(--warn)' : 'var(--ok)' }}>
                  {money(Math.abs(gst.net_payable))}
                </strong>
              </span>
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
              {gst.note}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function AgingCard({ title, report, nameKey }: {
  title: string
  report: AgingReport | null
  nameKey: 'by_contact' | 'by_vendor'
}) {
  if (!report) return <div className="skeleton" style={{ height: 200 }} />
  const rows = (report[nameKey] ?? []) as Array<{
    name: string; outstanding: number; days_overdue: number
  }>
  const max = Math.max(...report.buckets.map((b) => b.amount), 1)

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">{title}</span>
        <div className="grow" />
        <strong style={{ fontSize: 13 }}>{money(report.total_outstanding)}</strong>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 7 }}>
        {report.buckets.map((b) => (
          <div key={b.label} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 90px', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{b.label}</span>
            <div className="bar">
              <i style={{
                width: `${(b.amount / max) * 100}%`,
                background: b.label === 'Not due' ? 'var(--ok)' : 'var(--warn)',
              }} />
            </div>
            <span className="num" style={{ fontSize: 12 }}>{money(b.amount)}</span>
          </div>
        ))}
        {rows.length > 0 && (
          <div style={{ marginTop: 6, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
            {rows.slice(0, 5).map((r) => (
              <div key={r.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '2px 0' }}>
                <span>{r.name}{r.days_overdue > 0 && (
                  <span className="pill pill-warn" style={{ marginLeft: 6 }}>
                    {r.days_overdue}d
                  </span>
                )}</span>
                <span className="num">{money(r.outstanding)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
